export interface ScannedFile {
  fileName: string
  key: string
  location: string
}
type UploadState = { id: string; status: "processing" | "ready" | "failed"; file?: ScannedFile }
type Request = { url: string; method: "post" | "get"; data?: FormData; suppressErrorToast: boolean; timeoutMs: number }
type Transport = (args: Request) => Promise<{ data?: unknown; error?: unknown }>
const failure = () => new Error("This file could not be uploaded. Please try another file.")

function parseUpload(value: unknown): UploadState {
  if (!value || typeof value !== "object") throw failure()
  const upload = value as UploadState
  if (
    typeof upload.id !== "string" ||
    !/^[0-9a-f-]{36}$/i.test(upload.id) ||
    !["processing", "ready", "failed"].includes(upload.status)
  )
    throw failure()
  if (
    upload.status === "ready" &&
    (!upload.file ||
      typeof upload.file.location !== "string" ||
      !upload.file.location ||
      typeof upload.file.key !== "string" ||
      !upload.file.key ||
      typeof upload.file.fileName !== "string")
  )
    throw failure()
  return upload
}

export function waitForUploadPoll(signal: AbortSignal, milliseconds: number) {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(failure())
      return
    }
    const onAbort = () => {
      clearTimeout(timer)
      reject(failure())
    }
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort)
      resolve()
    }, milliseconds)
    signal.addEventListener("abort", onAbort, { once: true })
  })
}

export async function uploadScannedFiles(
  input: { files: FormData; type: string },
  signal: AbortSignal,
  request: Transport,
  options = { timeoutMs: 300_000, pollMs: 1000 },
): Promise<{ message: string; files: ScannedFile[] }> {
  const send = async (args: Request) => {
    if (signal.aborted) throw failure()
    const result = await request(args)
    if (result.error) throw result.error
    const data = result.data as Record<string, unknown> | undefined
    if (data?.success !== true) throw failure()
    return data
  }
  const response = await send({
    url: `/v1/file/uploads?type=${encodeURIComponent(input.type)}`,
    method: "post",
    data: input.files,
    timeoutMs: 300_000,
    suppressErrorToast: true,
  })
  if (!Array.isArray(response.uploads) || !response.uploads.length || response.uploads.length > 10) throw failure()
  let uploads = response.uploads.map(parseUpload)
  if (new Set(uploads.map((upload) => upload.id)).size !== uploads.length) throw failure()
  const deadline = Date.now() + options.timeoutMs
  for (;;) {
    if (signal.aborted || uploads.some((upload) => upload.status === "failed")) throw failure()
    if (uploads.every((upload) => upload.status === "ready"))
      return { message: "Files uploaded successfully!", files: uploads.map((upload) => upload.file!) }
    if (Date.now() >= deadline) throw failure()
    await waitForUploadPoll(signal, options.pollMs)
    uploads = await Promise.all(
      uploads.map(async (upload) => {
        if (upload.status === "ready") return upload
        const result = await send({
          url: `/v1/file/uploads/${upload.id}`,
          method: "get",
          timeoutMs: 45_000,
          suppressErrorToast: true,
        })
        const next = parseUpload(result.upload)
        if (next.id !== upload.id) throw failure()
        return next
      }),
    )
  }
}
